import { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Result, Space, Spin, Typography } from 'antd';
import { useAuth } from '../auth/AuthContext.tsx';
import { getTelegramLinkCode, linkTelegram, linkTelegramByCode, type TelegramLinkCodeAccount } from './api';
import { useDocumentTitle } from '../../shared/hooks/useDocumentTitle.ts';

const { Paragraph, Text } = Typography;

/**
 * Страница `/telegram/link` — подтверждение связки телеграм-аккаунта с
 * учётной записью Lens (docs/modules/telegram-bot.md §5.1).
 *
 * Открывается кнопкой «Связать аккаунт» из чата: Telegram сам добавляет в
 * адрес подписанные данные аккаунта. Подпись проверяет бэкенд, здесь —
 * только показать человеку обе стороны связки и спросить подтверждение.
 */
export function TelegramLinkPage(): JSX.Element {
  useDocumentTitle('Связать Telegram');
  const [params] = useSearchParams();
  const { code } = useParams<{ code?: string }>();
  const location = useLocation();
  const { status, user } = useAuth();
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<{ backfilledItems: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Данные Telegram переживают вход: SSO уводит человека на другой домен и
  // возвращает без query, поэтому кладём их в хранилище вкладки и достаём
  // обратно, если в адресе их уже нет.
  const payload = useMemo(() => {
    const keys = ['id', 'first_name', 'last_name', 'username', 'photo_url', 'auth_date', 'hash'];
    const entries: Record<string, string> = {};
    keys.forEach((key) => {
      const value = params.get(key);
      if (value) entries[key] = value;
    });
    try {
      if (entries.hash) {
        sessionStorage.setItem('telegram-link-payload', JSON.stringify(entries));
        return entries;
      }
      const saved = sessionStorage.getItem('telegram-link-payload');
      if (saved) return JSON.parse(saved) as Record<string, string>;
    } catch {
      // Приватный режим браузера — просто работаем без запасного хранилища.
    }
    return entries;
  }, [params]);

  const hasPayload = Boolean(payload.id && payload.hash && payload.auth_date);
  // Второй путь: персональная ссылка от бота. Аккаунт в ней не назван —
  // его отдаёт сервер по коду, чтобы ссылку нельзя было подделать.
  const [codeAccount, setCodeAccount] = useState<TelegramLinkCodeAccount | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  useEffect(() => {
    if (!code || status !== 'authenticated') return;
    getTelegramLinkCode(code)
      .then((res) => setCodeAccount(res.account))
      .catch((err) => setCodeError(err instanceof Error ? err.message : 'Ссылка недействительна'));
  }, [code, status]);


  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  // Вход возвращает сюда же вместе с параметрами — подпись не теряется.
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  const telegramName = code
    ? [codeAccount?.firstName, codeAccount?.lastName].filter(Boolean).join(' ')
    : [payload.first_name, payload.last_name].filter(Boolean).join(' ');
  const telegramUsername = code ? codeAccount?.username : payload.username;

  const handleLink = async () => {
    setPending(true);
    setError(null);
    try {
      const res = code ? await linkTelegramByCode(code) : await linkTelegram(payload);
      try {
        sessionStorage.removeItem('telegram-link-payload');
      } catch {
        // не критично
      }
      setResult({ backfilledItems: res.backfilledItems });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось связать аккаунт');
    } finally {
      setPending(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <Card style={{ maxWidth: 520, width: '100%' }} data-testid="telegram-link-page">
        {result ? (
          <Result
            status="success"
            title="Аккаунт связан"
            subTitle={
              result.backfilledItems > 0
                ? `Готово. Заодно проставили вас автором в ${result.backfilledItems} записях, созданных из чатов раньше.`
                : 'Готово, можно вернуться в чат.'
            }
            extra={
              <Button type="primary" href="/">
                Открыть Lens
              </Button>
            }
          />
        ) : code && codeError ? (
          <Result status="warning" title="Ссылка недействительна" subTitle={codeError} />
        ) : code && !codeAccount ? (
          <Spin />
        ) : !code && !hasPayload ? (
          <Result
            status="warning"
            title="Нет данных Telegram"
            subTitle="Откройте эту страницу кнопкой «Связать аккаунт» из чата — Telegram добавит к ссылке подписанные данные."
          />
        ) : (
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Typography.Title level={4} style={{ marginBottom: 0 }}>
              Связать аккаунт Telegram
            </Typography.Title>
            <Paragraph>
              Связать Telegram <Text strong>{telegramName || 'без имени'}</Text>
              {telegramUsername ? <Text strong> (@{telegramUsername})</Text> : null} с учётной записью{' '}
              <Text strong>{user?.displayName}</Text>?
            </Paragraph>
            <Paragraph type="secondary">
              После этого вы будете значиться автором записей, созданных из рабочих чатов, — включая те,
              что уже созданы до связки.
            </Paragraph>
            {error && <Alert type="error" showIcon message={error} />}
            <Space>
              <Button type="primary" loading={pending} onClick={handleLink} data-testid="telegram-link-confirm">
                Связать
              </Button>
              <Button href="/">Отмена</Button>
            </Space>
          </Space>
        )}
      </Card>
    </div>
  );
}
